"use strict";
/*!
Copyright (C) 2020 Evolved Binary

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports._test_private_document = exports.NODES = void 0;
exports.deleteUndefined = deleteUndefined;
exports.document = document;
const lwdita_ast_1 = require("@evolvedbinary/lwdita-ast");
const schema_1 = require("./schema");
const utils_1 = require("./utils");
/**
 * Removes undefined attributes from an object
 *
 * @param object  - Generic object
 * @returns object - The object with undefined attributes removed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deleteUndefined(object) {
    if (object) {
        for (const key in object) {
            if (typeof object[key] === 'undefined') {
                delete (object[key]);
            }
        }
    }
    return object;
}
/**
 * A map of JDita nodes that require a customized transformation into ProseMirror nodes,
 * e.g. because their content model can't be applied directly as valid HTML:
 * `audio`, `video`, `image`, `text`.
 *
 * Instead of using the defaultTravel function, we use the special node function
 * The following 4 nodes (audio, video, image, text) are
 * treated in a customized way instead of applying the defaultTravel() function:
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
exports.NODES = {
    video: (value, parent) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        // Create a new object with title and poster attributes, if they exist:
        // the desc and the video-poster children will be assigned as new attributes to the video element
        // undefined attributes will be removed
        const attrs = deleteUndefined(Object.assign(Object.assign({}, value.attributes), { title: (_d = (_c = (_b = (_a = value.children) === null || _a === void 0 ? void 0 : _a.find(child => child.nodeName === 'desc')) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.content, poster: (_g = (_f = (_e = value.children) === null || _e === void 0 ? void 0 : _e.find(child => child.nodeName === 'video-poster')) === null || _f === void 0 ? void 0 : _f.attributes) === null || _g === void 0 ? void 0 : _g.href }));
        // Create a new array with the fallback, media-source and media-track children
        // that will be assigned as the content of the video element
        const content = [];
        (_h = value.children) === null || _h === void 0 ? void 0 : _h.forEach(child => {
            if (['fallback', 'media-source', 'media-track'].includes(child.nodeName)) {
                content.push(child);
            }
        });
        // Return the video object with the new attributes and content
        return { type: (0, utils_1.lwditaNodeNameToSchemaNodeName)(value.nodeName, nodeAllowsMixedContent(parent.nodeName)), attrs, content: content === null || content === void 0 ? void 0 : content.map(child => travel(child, value)) };
    },
    audio: (value, parent) => {
        var _a, _b, _c, _d, _e;
        // Create a new object with a title attribute, if it exists:
        // the `desc` child will be assigned as a new attribute to the audio element,
        // undefined attributes will be removed
        const attrs = deleteUndefined(Object.assign(Object.assign({}, value.attributes), { title: (_d = (_c = (_b = (_a = value.children) === null || _a === void 0 ? void 0 : _a.find(child => child.nodeName === 'desc')) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.content }));
        // Create a new array with the fallback, media-source and media-track children
        // that will be assigned as the content of the audio element
        const content = [];
        (_e = value.children) === null || _e === void 0 ? void 0 : _e.forEach(child => {
            if (['fallback', 'media-source', 'media-track'].includes(child.nodeName)) {
                content.push(child);
            }
        });
        // Return the audio object with the new attributes and content
        return { type: (0, utils_1.lwditaNodeNameToSchemaNodeName)(value.nodeName, nodeAllowsMixedContent(parent.nodeName)), attrs, content: content === null || content === void 0 ? void 0 : content.map(child => travel(child, value)) };
    },
    image: (value, parent) => {
        var _a;
        if (value.children
            && value.children[0].nodeName === 'alt'
            && ((_a = value.children[0]) === null || _a === void 0 ? void 0 : _a.children)
            && value.children[0].children[0].nodeName == 'text') {
            const attrs = deleteUndefined(Object.assign(Object.assign({}, value.attributes), { alt: value.children[0].children[0].content }));
            const result = { type: (0, utils_1.lwditaNodeNameToSchemaNodeName)('image', nodeAllowsMixedContent(parent.nodeName)), attrs };
            return result;
        }
        return defaultTravel(value, parent);
    },
    text: (value) => ({ type: 'text', text: value.content, attrs: {} }),
};
/**
 * Checks if the node allows mixed content
 * @param value - The JDita node
 * @returns true if the node allows mixed content
 */
function nodeAllowsMixedContent(nodeName) {
    if (nodeName === 'doc') {
        return false;
    }
    const nodeClass = (0, lwdita_ast_1.getNodeClass)(nodeName);
    const nodeInstance = new nodeClass({});
    return nodeInstance.allowsMixedContent();
}
/**
 * Transforms the JDita document into a proper ProseMirror document
 *
 * @param value - The JDita node
 * @param parent - The parent JDita node
 * @returns The transformed JDita node
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultTravel(value, parent) {
    var _a;
    // children will become content
    const content = (_a = value.children) === null || _a === void 0 ? void 0 : _a.map(child => travel(child, value));
    // attributes will become attrs
    const attrs = value.attributes || {};
    // remove undefined attributes
    deleteUndefined(attrs);
    // node name will become type
    let type = (0, schema_1.defaultNodeName)(value.nodeName);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result;
    // IS_MARK is the array  `u, s, b, sup, sub`
    if (schema_1.IS_MARK.indexOf(value.nodeName) > -1) {
        if ((content === null || content === void 0 ? void 0 : content.length) === 1) {
            result = content[0];
            result.marks = [{ type }];
        }
    }
    else {
        type = (0, utils_1.lwditaNodeNameToSchemaNodeName)(type, nodeAllowsMixedContent(parent.nodeName));
        result = {
            type,
            attrs,
        };
        if (content) {
            result.content = content;
        }
    }
    return result;
}
/**
 * Traverses the JDita document and generates a ProseMirror document
 *
 * @param value - The JDita node
 * @param parent - The parent JDita node
 * @returns The transformed JDita node
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function travel(value, parent) {
    // if it's a special node, use the special node function,
    // otherwise use the default travel function
    const result = (exports.NODES[value.nodeName] || defaultTravel)(value, parent);
    // if the node is not a document and has attributes, set the parent attribute
    if (value.nodeName !== 'doc' && result && result.attrs) {
        result.attrs.parent = parent.nodeName;
    }
    return result;
}
/**
 * Transforms the JDita document
 * into a Schema compliant JDita document
 *
 * @example
 * Here's an example input:
 * ```
 * {
 *   "nodeName": "document",
 *   "children": [
 *     {
 *       "nodeName": "topic",
 *       "attributes": {
 *       "id": "intro-product"
 *     },
 *     {
 *       "nodeName": "title",
 *       "attributes": {},
 *       "children": [
 *         {
 *           "nodeName": "text",
 *           "content": "Overview"
 *         }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * @example
 * Here's an example output of the transformation `travel(jdita, jdita)`:
 * ```
 * {
 *   "type": "doc",
 *   "attrs": {},
 *   "content": [
 *     {
 *       "type": "topic",
 *       "attrs": {
 *         "id": "intro-product",
 *         "parent": "doc"
 *       },
 *       "content": [
 *         {
 *           "type": "title",
 *           "attrs": {
 *             "parent": "topic"
 *           },
 *           "content": [
 *             {
 *               "type": "text",
 *               "text": "Overview",
 *               "attrs": {
 *                 "parent": "title"
 *               }
 *             }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * @param jdita - the JDita document
 * @returns transformed JDita document
 */
function document(jdita) {
    if (jdita.nodeName === 'document') {
        jdita.nodeName = 'doc';
        /*
        Parameter `jdita` is representing the root of JDita document.
        We pass in the root node as the first parameter, and since it's the root node,
        it's also the parent node, which is passed as the second parameter.
        This will return the output of the transformation.
        */
        return travel(jdita, jdita);
    }
    throw new Error('jdita must be a document');
}
//Escape hatch for Unit Testing due to a lack of “package-private” accessibility scope in TypeScript
exports._test_private_document = {
    deleteUndefined,
    defaultTravel,
    travel,
};
//# sourceMappingURL=document.js.map